import React, { createContext, useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { authService, LoginCredentials } from '../services/authService';
import { apiService } from '../services/api';
import { userService, UsuarioInfoResponse } from '../services/userService';

// User interface
interface User {
    id: string;
    name: string;
    email: string;
    documento: string;
    role: string;
    su: boolean;
}

// Utility function to decode JWT token
const decodeJwtToken = (token: string): any => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Error decoding JWT token:', error);
        return null;
    }
};

// Utility function to extract user from JWT token
const extractUserFromToken = (token: string): User | null => {
    try {
        const decoded = decodeJwtToken(token);
        if (decoded && decoded.sub) {
            return {
                id: decoded.sub,
                name: decoded.name || decoded.sub,
                email: decoded.sub,
                documento: decoded.documento || '',
                role: decoded.role || 'user',
                su: decoded.su || false
            };
        }
        return null;
    } catch (error) {
        console.error('Error extracting user from token:', error);
        return null;
    }
};

// Utility function to fetch user info from API and merge with token data
const fetchUserInfo = async (token: string): Promise<User | null> => {
    try {
        // Get basic user info from API
        const userInfo: UsuarioInfoResponse = await userService.getUserInfo();

        // Extract sensitive data from token
        const tokenData = extractUserFromToken(token);

        if (tokenData) {
            return {
                id: userInfo.documento, // Using documento as ID
                name: userInfo.nome,
                email: userInfo.email,
                documento: userInfo.documento,
                role: tokenData.role, // Keep role from token
                su: tokenData.su // Keep su flag from token
            };
        }

        return null;
    } catch (error) {
        console.error('Error fetching user info:', error);
        return null;
    }
};

interface AuthContextData {
    isAuthenticated: boolean;
    token: string | null;
    user: User | null;
    login: (credentials: LoginCredentials) => Promise<void>;
    logout: () => Promise<void>;
    loading: boolean;
    error: string | null;
    clearError: () => void;
    refreshUser: () => void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC = ({ children }) => {
    const history = useHistory();
    const [token, setToken] = useState<string | null>(() => {
        return apiService.getAuthToken();
    });
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Function to refresh user data from API
    const refreshUser = async () => {
        const currentToken = apiService.getAuthToken();
        if (currentToken && currentToken !== token) {
            try {
                const userFromApi = await fetchUserInfo(currentToken);
                setUser(userFromApi);
                setToken(currentToken);
            } catch (error) {
                console.error('Error refreshing user data:', error);
                setUser(null);
            }
        } else if (!currentToken) {
            setUser(null);
            setToken(null);
        }
    };

    useEffect(() => {
        // Initialize authentication state
        const initializeAuth = async () => {
            if (token) {
                try {
                    // Fetch user info from API
                    const userFromApi = await fetchUserInfo(token);
                    setUser(userFromApi);
                } catch (error) {
                    console.error('Error initializing auth:', error);
                    setToken(null);
                    setUser(null);
                }
            } else {
                // No valid token, clear state
                setToken(null);
                setUser(null);
            }
            setLoading(false);
        };

        initializeAuth();
    }, []); // Remove token dependency to avoid loops

    // Listen for token changes
    useEffect(() => {
        const checkTokenValidity = async () => {
            const currentToken = apiService.getAuthToken();
            if (currentToken !== token) {
                setToken(currentToken);
                if (currentToken) {
                    try {
                        const userFromApi = await fetchUserInfo(currentToken);
                        setUser(userFromApi);
                    } catch (error) {
                        console.error('Error fetching user info on token change:', error);
                        setUser(null);
                    }
                } else {
                    setUser(null);
                }
            }
        };

        // Only check when component mounts, not on every token change
        checkTokenValidity();
    }, []); // Remove token dependency to avoid loops

    const login = async (credentials: LoginCredentials) => {
        try {
            setLoading(true);
            setError(null);
            const response = await authService.login(credentials);

            setToken(response.access_token);

            // Fetch user info from API
            const userFromApi = await fetchUserInfo(response.access_token);
            setUser(userFromApi);

            history.push('/home');
        } catch (error) {
            console.error('Login failed:', error);
            setError(error instanceof Error ? error.message : 'Login failed');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            setLoading(true);
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setToken(null);
            setUser(null);
            setLoading(false);
            // Navigate to login page
            history.push('/login');
        }
    };

    const clearError = () => {
        setError(null);
    };

    return (
        <AuthContext.Provider value={{
            isAuthenticated: !!token,
            token,
            user,
            login,
            logout,
            loading,
            error,
            clearError,
            refreshUser
        }}>
            {children}
        </AuthContext.Provider>
    );
};

// Create a wrapper component to provide history to AuthProvider
export const AuthProviderWithHistory: React.FC = ({ children }) => {
    return <AuthProvider>{children}</AuthProvider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 